import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Calendar, Clock, MapPin, Car, Filter, RefreshCw, CalendarPlus, Navigation } from 'lucide-react';
import { getClientBookings } from '../api';

const ClientBookings = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [total, setTotal] = useState(0);

  const statusColors = {
    pending_payment: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800'
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client/auth/me`, { credentials: 'include' });
        if (!response.ok) navigate('/portal/login');
      } catch { navigate('/portal/login'); }
    };
    checkAuth();
  }, [navigate]);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const data = await getClientBookings(params);
      setBookings(data.bookings || []);
      setTotal(data.total || 0);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => { loadBookings(); }, [statusFilter]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleRebook = (booking) => {
    const params = new URLSearchParams();
    if (booking.serviceType) params.set('service', booking.serviceType);
    if (booking.pickupLocation) params.set('pickup', booking.pickupLocation);
    if (booking.dropoffLocation) params.set('dropoff', booking.dropoffLocation);
    navigate(`/booking?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-gray-100" data-testid="client-bookings-page">
      <header className="bg-black text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/portal')} className="text-gray-300 hover:text-white">
              <ArrowLeft className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Back</span>
            </Button>
            <h1 className="text-lg sm:text-xl font-bold">My Bookings</h1>
          </div>
          <span className="text-sm text-gray-400">{total} total</span>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Filter */}
        <Card className="p-3 sm:p-4 mb-6 border-gray-200">
          <div className="flex items-center gap-3">
            <Filter className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="booking-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Bookings</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="pending_payment">Pending</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {loading ? (
          <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto" /></div>
        ) : bookings.length === 0 ? (
          <Card className="p-10 sm:p-12 text-center border-gray-200">
            <Car className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-black mb-2">No bookings found</h3>
            <p className="text-gray-600 mb-6">Ready for your next adventure?</p>
            <Button onClick={() => navigate('/booking')} className="bg-red-600 hover:bg-red-700 text-white">Book a Ride</Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => (
              <Card key={booking.bookingId} className="p-4 sm:p-6 border-gray-200 hover:shadow-md transition-shadow" data-testid={`booking-card-${booking.bookingId}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                    <div className="h-12 w-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Car className="h-6 w-6 text-red-600" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-mono text-gray-500">{booking.bookingId}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[booking.status] || 'bg-gray-100 text-gray-800'}`}>
                          {booking.status?.replace('_', ' ')}
                        </span>
                      </div>
                      <h3 className="text-base sm:text-lg font-bold text-black">{booking.serviceType}</h3>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-600">
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{booking.pickupLocation}</span>
                        <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDate(booking.pickupDate)}</span>
                        {booking.pickupTime && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{booking.pickupTime}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between sm:flex-col sm:items-end gap-2 flex-shrink-0">
                    <div className="text-right">
                      <p className="text-xl font-bold text-black">${booking.totalPrice}</p>
                      {booking.hours && <p className="text-xs text-gray-500">{booking.hours} hrs</p>}
                    </div>
                    {(booking.status === 'confirmed' || booking.status === 'in_progress') && (
                      <Button
                        onClick={(e) => { e.stopPropagation(); navigate(`/portal/track/${booking.bookingId}`); }}
                        size="sm"
                        className="bg-red-600 hover:bg-red-700 text-white text-xs"
                        data-testid={`track-btn-${booking.bookingId}`}
                      >
                        <Navigation className="h-3 w-3 mr-1" /> Track
                      </Button>
                    )}
                    {booking.status === 'completed' && (
                      <Button
                        onClick={(e) => { e.stopPropagation(); handleRebook(booking); }}
                        size="sm"
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-600 hover:text-white text-xs"
                        data-testid={`rebook-btn-${booking.bookingId}`}
                      >
                        <RefreshCw className="h-3 w-3 mr-1" /> Rebook
                      </Button>
                    )}
                    {(booking.status === 'confirmed' || booking.status === 'pending_payment') && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/calendar/booking/${booking.bookingId}/google`}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border border-gray-300 text-gray-600 hover:bg-black hover:text-white transition-colors"
                        data-testid={`gcal-btn-${booking.bookingId}`}>
                        <CalendarPlus className="h-3 w-3" /> Google Cal
                      </a>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientBookings;
